/**
 * Kostnadsberäkning för svensk elkund med solceller.
 *
 * Stöder tre delar:
 *   1. importPrice - vad det kostar att köpa 1 kWh från nätet en viss timme
 *   2. exportRevenue - vad man får för 1 kWh man säljer en viss timme
 *   3. applyMonthly - månadens fasta avgifter, effekttariff, tröskelmodell
 *
 * 60-öringen (mikroproduktionsavdraget) tas bort 2026-01-01 enligt riksdag-
 * beslutet 15 maj 2025. Funktionen exportRevenue läser timstämpeln per
 * timme så data som spänner över årsskiftet räknas korrekt.
 */

import type {
  HourPoint,
  ResultHour,
  TaxConfig,
  SupplierConfig,
  GridFeeConfig,
  AncillaryServiceConfig,
  BatteryConfig,
} from "./types";
import { calculateAncillaryRevenue } from "./extra-costs";

export const DEFAULT_TAX_2026: TaxConfig = {
  // Energiskatt 2026: 36 öre/kWh exkl moms (45 öre inkl moms).
  // Sänkt från 43.9 öre 2025 enligt riksdagsbeslut.
  energyTaxPerKWh: 0.36,
  vatRate: 0.25,
  // 60-öringen (skattereduktion på export) gäller bara t.o.m. 2025-12-31.
  // Vi modellerar KOMMANDE år givet förgångna år som mall, så reduktionen
  // räknas inte med — den är borta i framtiden. Sätt cutoff långt i
  // förflutet så credit aldrig appliceras oavsett när datan kommer från.
  microproducerCreditPerKWh: 0,
  microproducerCreditEndDate: "2020-01-01T00:00:00+01:00",
};

/**
 * Bestäm vilken tröskelnivå som gäller givet totalimport för perioden.
 * Returnerar effektiva avgifter inkl tier-växling om threshold passeras.
 */
export function resolveTierForPeriod(
  grid: GridFeeConfig,
  totalAnnualImportKWh: number,
): { fixedMonthly: number; transmissionPerKWh: number } {
  if (!grid.tier) {
    return {
      fixedMonthly: grid.fixedMonthlySek,
      transmissionPerKWh: grid.transmissionPerKWh,
    };
  }
  if (totalAnnualImportKWh > grid.tier.thresholdKWhPerYear) {
    return {
      fixedMonthly: grid.tier.aboveTier.fixedMonthlySek,
      transmissionPerKWh: grid.tier.aboveTier.transmissionPerKWh,
    };
  }
  return {
    fixedMonthly: grid.fixedMonthlySek,
    transmissionPerKWh: grid.transmissionPerKWh,
  };
}

/**
 * Pris för att importera 1 kWh under timmen, inkl moms.
 * Tröskelmodellens tier väljs på baserat på årlig totalimport som
 * skickas in från anroparen (vanligen baseline- eller battery-scenariots
 * totalimport, vilket gör att tariffvalet kan skilja sig mellan dessa).
 */
export function importPricePerKWh(
  hour: HourPoint,
  tax: TaxConfig,
  supplier: SupplierConfig,
  grid: GridFeeConfig,
  annualImportContext: number,
): number {
  const tier = resolveTierForPeriod(grid, annualImportContext);
  const energyExVat =
    hour.spotPrice +
    supplier.importMarkupPerKWh +
    tax.energyTaxPerKWh +
    tier.transmissionPerKWh;
  return energyExVat * (1 + tax.vatRate);
}

/**
 * Intäkt för att exportera 1 kWh under timmen (kr/kWh).
 * Inkluderar:
 *   - spot - eventuellt avdrag från elhandlare
 *   - nätnytta-kompensation från nätbolaget (kr/kWh)
 *   - 60-öringen om datum är före gränsen
 *
 * Mikroproducenter momsregistrerar sig oftast inte (omsättning under
 * 120 000 kr/år) så export är momsfri.
 */
export function exportRevenuePerKWh(
  hour: HourPoint,
  tax: TaxConfig,
  supplier: SupplierConfig,
  grid: GridFeeConfig,
): number {
  const base =
    hour.spotPrice -
    supplier.exportMarkdownPerKWh +
    supplier.exportBonusPerKWh +
    grid.feedInCompensationPerKWh;

  const hourTime = new Date(hour.from).getTime();
  const cutoff = new Date(tax.microproducerCreditEndDate).getTime();
  const creditApplies = hourTime <= cutoff;

  return creditApplies ? base + tax.microproducerCreditPerKWh : base;
}

/**
 * Nettokostnad för en timme givet faktisk import och export.
 * Positiv = kostnad, negativ = intäkt.
 */
export function hourlyEnergyCost(
  hour: HourPoint,
  importKWh: number,
  exportKWh: number,
  tax: TaxConfig,
  supplier: SupplierConfig,
  grid: GridFeeConfig,
  annualImportContext: number,
): number {
  const importCost =
    importKWh * importPricePerKWh(hour, tax, supplier, grid, annualImportContext);
  const exportIncome = exportKWh * exportRevenuePerKWh(hour, tax, supplier, grid);
  return importCost - exportIncome;
}

/**
 * Månadens fasta avgifter och effekttariff.
 * Tröskelvalet baseras på ANNUALISERAD totalimport så vi väljer rätt
 * tier även om perioden är partiell.
 */
export function calculateMonthlyFees(
  hours: ResultHour[],
  grid: GridFeeConfig,
  tax: TaxConfig,
): { totalSek: number; byMonth: Map<string, number> } {
  const totalRawImport = hours.reduce((s, h) => s + h.importKWh, 0);
  const monthsCovered = new Set(hours.map((h) => h.from.slice(0, 7))).size;
  const annualImportEstimate =
    totalRawImport * (12 / Math.max(monthsCovered, 1));
  const tier = resolveTierForPeriod(grid, annualImportEstimate);

  const byMonthHours = new Map<string, ResultHour[]>();
  for (const h of hours) {
    const ym = h.from.slice(0, 7);
    const list = byMonthHours.get(ym);
    if (list) list.push(h);
    else byMonthHours.set(ym, [h]);
  }

  const byMonth = new Map<string, number>();
  let total = 0;

  for (const [ym, monthHours] of byMonthHours) {
    let fee = tier.fixedMonthly * (1 + tax.vatRate);

    if (
      grid.powerTariff &&
      grid.powerTariff.pricePerKWPerMonth > 0 &&
      (grid.powerTariff.activateFromYear ?? 0) === 0
    ) {
      const topN = monthHours
        .map((h) => h.importKWh)
        .sort((a, b) => b - a)
        .slice(0, grid.powerTariff.topHoursAveraged);
      const avgTopKW =
        topN.reduce((s, v) => s + v, 0) / Math.max(topN.length, 1);

      let pricePerKW = grid.powerTariff.pricePerKWPerMonth;
      if (grid.powerTariff.seasonalDifferentiation) {
        const monthNum = parseInt(ym.split("-")[1] ?? "0", 10);
        if (
          grid.powerTariff.seasonalDifferentiation.winterMonths.includes(monthNum)
        ) {
          pricePerKW *= grid.powerTariff.seasonalDifferentiation.winterMultiplier;
        }
      }
      fee += avgTopKW * pricePerKW * (1 + tax.vatRate);
    }

    byMonth.set(ym, fee);
    total += fee;
  }

  return { totalSek: total, byMonth };
}
/**
 * Komponenter i besparingen, för dekomposition i UI:t.
 * Positiva tal = besparing (vinst), negativa tal = förlust.
 *
 * Räknar baseline (utan batteri) minus battery-scenariot för varje
 * kostnadsdel. Tröskelmodellen tillämpas separat per scenario eftersom
 * batterits agerande kan flytta dig mellan tier-nivåer.
 */
export interface SavingsBreakdown {
  /** Sparat på minskad import: spotkostnad. */
  importSpotSavings: number;
  /** Sparat på minskad import: elhandlarens påslag. */
  importMarkupSavings: number;
  /** Sparat på minskad import: elöverföring (nätbolagets per-kWh). */
  importTransmissionSavings: number;
  /** Sparat på minskad import: energiskatt. */
  importEnergyTaxSavings: number;
  /** Sparat på minskad import: moms på allt ovan. */
  importVatSavings: number;

  /** Förlorat på minskad export: spotintäkt. */
  exportSpotLoss: number;
  /** Förlorat på minskad export: nätnytta. */
  exportFeedInLoss: number;
  /** Förlorat på minskad export: 60-öringen (bara timmar före gränsen). */
  exportCreditLoss: number;
  /** Förlorat på minskad export: elhandlarens bonus om sådan. */
  exportBonusLoss: number;

  /** Förändring i fasta månadsavgifter (kan vara positiv eller negativ
   * beroende på tier-byte). */
  fixedFeeDelta: number;
  /** Förändring i effekttariff-kostnad (positivt = sparat). */
  powerTariffDelta: number;

  /** Stödtjänster (alltid positiv om aktiv). */
  ancillaryRevenue: number;

  /** Summan av alla komponenter ovan = total besparing. */
  totalSavings: number;

  /** Diagnostik: visar tier som valts för respektive scenario. */
  baselineTier: "below" | "above" | "none";
  batteryTier: "below" | "above" | "none";
}

export function decomposeSavings(
  baselineHours: HourPoint[],
  batteryHours: ResultHour[],
  scenario: {
    tax: TaxConfig;
    supplier: SupplierConfig;
    grid: GridFeeConfig;
    ancillary: AncillaryServiceConfig;
    battery: BatteryConfig;
  },
  monthsCovered: number,
): SavingsBreakdown {
  const { tax, supplier, grid, ancillary, battery } = scenario;

  const baselineAnnualImport = baselineHours.reduce(
    (s, h) => s + h.importKWh,
    0,
  );
  const batteryAnnualImport = batteryHours.reduce(
    (s, h) => s + h.importKWh,
    0,
  );

  const baselineTier = resolveTierForPeriod(grid, baselineAnnualImport);
  const batteryTier = resolveTierForPeriod(grid, batteryAnnualImport);

  let importSpotSavings = 0;
  let importMarkupSavings = 0;
  let importTransmissionSavings = 0;
  let importEnergyTaxSavings = 0;
  let importVatSavings = 0;

  let exportSpotLoss = 0;
  let exportFeedInLoss = 0;
  let exportCreditLoss = 0;
  let exportBonusLoss = 0;

  const cutoff = new Date(tax.microproducerCreditEndDate).getTime();

  for (let i = 0; i < baselineHours.length; i++) {
    const bh = baselineHours[i];
    const rh = batteryHours[i];
    if (!bh || !rh) continue;

    const deltaImport = bh.importKWh - rh.importKWh; // positiv = batteriet sparade import
    const deltaExport = bh.exportKWh - rh.exportKWh; // positiv = batteriet absorberade export

    // Importsida: vi sparar dessa kostnader
    importSpotSavings += deltaImport * bh.spotPrice;
    importMarkupSavings += deltaImport * supplier.importMarkupPerKWh;
    importEnergyTaxSavings += deltaImport * tax.energyTaxPerKWh;

    // Tier kan skilja mellan scenarier — behandla per scenario:
    // Vi sparar baselineTier.transmissionPerKWh för varje undviken import,
    // men batteriet kan istället ha råkat in i en annan tier som kostar
    // mer eller mindre. Approximerar dock genom att räkna sparandet på
    // genomsnittlig tier-rate (linjär approx):
    const transmissionRate =
      bh.importKWh > 0
        ? baselineTier.transmissionPerKWh
        : batteryTier.transmissionPerKWh;
    importTransmissionSavings += deltaImport * transmissionRate;

    // Moms på allt ovan
    const exVatSavedThisHour =
      deltaImport *
      (bh.spotPrice +
        supplier.importMarkupPerKWh +
        tax.energyTaxPerKWh +
        transmissionRate);
    importVatSavings += exVatSavedThisHour * tax.vatRate;

    // Exportsida: vi förlorar dessa intäkter (positiva tal = förlust)
    exportSpotLoss += deltaExport * bh.spotPrice;
    exportFeedInLoss += deltaExport * grid.feedInCompensationPerKWh;
    exportBonusLoss += deltaExport * supplier.exportBonusPerKWh;

    const hourTime = new Date(bh.from).getTime();
    if (hourTime <= cutoff) {
      exportCreditLoss += deltaExport * tax.microproducerCreditPerKWh;
    }
  }

  // Fasta avgifter: skillnaden mellan tier för baseline och batteri
  const monthlyFactor = 12 / Math.max(monthsCovered, 1);
  const fixedFeeBaseline =
    baselineTier.fixedMonthly * 12 * (1 + tax.vatRate); // annualiserat
  const fixedFeeBattery =
    batteryTier.fixedMonthly * 12 * (1 + tax.vatRate);
  const fixedFeeDelta = fixedFeeBaseline - fixedFeeBattery;

  // Effekttariff (om aktiv just nu — activateFromYear=0 betyder aktiv från år 1)
  let powerTariffDelta = 0;
  if (
    grid.powerTariff &&
    grid.powerTariff.pricePerKWPerMonth > 0 &&
    (grid.powerTariff.activateFromYear ?? 0) === 0
  ) {
    // Approximation: månadens medeltopp per scenario
    const baselineMonthlyTops = new Map<string, number[]>();
    const batteryMonthlyTops = new Map<string, number[]>();
    for (const h of baselineHours) {
      const ym = h.from.slice(0, 7);
      const list = baselineMonthlyTops.get(ym) ?? [];
      list.push(h.importKWh);
      baselineMonthlyTops.set(ym, list);
    }
    for (const h of batteryHours) {
      const ym = h.from.slice(0, 7);
      const list = batteryMonthlyTops.get(ym) ?? [];
      list.push(h.importKWh);
      batteryMonthlyTops.set(ym, list);
    }
    const n = grid.powerTariff.topHoursAveraged;
    const seasonMultiplier = (monthKey: string): number => {
      if (!grid.powerTariff?.seasonalDifferentiation) return 1;
      const month = parseInt(monthKey.slice(5, 7), 10);
      return grid.powerTariff.seasonalDifferentiation.winterMonths.includes(month)
        ? grid.powerTariff.seasonalDifferentiation.winterMultiplier
        : 1;
    };
    let baselinePowerCost = 0;
    let batteryPowerCost = 0;
    for (const [monthKey, vals] of baselineMonthlyTops) {
      const top = vals.sort((a, b) => b - a).slice(0, n);
      const avg = top.reduce((s, v) => s + v, 0) / Math.max(top.length, 1);
      baselinePowerCost +=
        avg *
        grid.powerTariff.pricePerKWPerMonth *
        seasonMultiplier(monthKey) *
        (1 + tax.vatRate);
    }
    for (const [monthKey, vals] of batteryMonthlyTops) {
      const top = vals.sort((a, b) => b - a).slice(0, n);
      const avg = top.reduce((s, v) => s + v, 0) / Math.max(top.length, 1);
      batteryPowerCost +=
        avg *
        grid.powerTariff.pricePerKWPerMonth *
        seasonMultiplier(monthKey) *
        (1 + tax.vatRate);
    }
    powerTariffDelta = (baselinePowerCost - batteryPowerCost) * monthlyFactor;
  }

  const ancillaryRevenue = ancillary.enabled
    ? calculateAncillaryRevenue(ancillary, battery).netAnnualAverage
    : 0;

  // Annualisera tim-baserade komponenter
  const hourlyFactor = 8760 / Math.max(baselineHours.length, 1);
  importSpotSavings *= hourlyFactor;
  importMarkupSavings *= hourlyFactor;
  importTransmissionSavings *= hourlyFactor;
  importEnergyTaxSavings *= hourlyFactor;
  importVatSavings *= hourlyFactor;
  exportSpotLoss *= hourlyFactor;
  exportFeedInLoss *= hourlyFactor;
  exportCreditLoss *= hourlyFactor;
  exportBonusLoss *= hourlyFactor;

  // Fasta avgifter är redan månadsbaserade — annualisera mot monthsCovered
  const fixedFeeDeltaAnnual = (fixedFeeDelta / 12) * monthlyFactor * 12;

  const totalSavings =
    importSpotSavings +
    importMarkupSavings +
    importTransmissionSavings +
    importEnergyTaxSavings +
    importVatSavings -
    exportSpotLoss -
    exportFeedInLoss -
    exportCreditLoss -
    exportBonusLoss +
    fixedFeeDeltaAnnual +
    powerTariffDelta +
    ancillaryRevenue;

  function describeTier(tierRate: number): "below" | "above" | "none" {
    if (!grid.tier) return "none";
    return tierRate === grid.tier.aboveTier.transmissionPerKWh
      ? "above"
      : "below";
  }

  return {
    importSpotSavings,
    importMarkupSavings,
    importTransmissionSavings,
    importEnergyTaxSavings,
    importVatSavings,
    exportSpotLoss,
    exportFeedInLoss,
    exportCreditLoss,
    exportBonusLoss,
    fixedFeeDelta: fixedFeeDeltaAnnual,
    powerTariffDelta,
    ancillaryRevenue,
    totalSavings,
    baselineTier: describeTier(baselineTier.transmissionPerKWh),
    batteryTier: describeTier(batteryTier.transmissionPerKWh),
  };
}
